import { errorGif } from '@mode/shared/ui-images';
import * as angular from 'angular';
import _ from 'lodash';
import '../../modules/mode-components';
import { Observable } from 'rxjs';
import { ThoughtspotIntegrationService } from '@mode/shared/util-thoughtspot-integrated';
import { ReportHistoryService } from './report-history.service';

class ReportHistoryController {
  static $inject = [
    '$scope',
    'QueryModel',
    '$q',
    'ResultModel',
    '$timeout',
    '$window',
    'ThoughtspotIntegrationService',
  ];

  public onDismiss: () => void;
  public onClose: ({ queryRun }: { queryRun: any }) => void;
  public errorImage: string;
  public isAnalystStudio$: Observable<boolean>;

  private runPreview: any;
  private query: any;
  private queryRunsCollection: any;
  private queryRunsParams: Readonly<any> = Object.freeze({
    includes: {
      view: 'attrs',
      query_runs: ['result', 'executed_by'],
    },
    refresh: false,
  });
  private state: {
    selectedTab: 'raw' | 'rendered' | 'data';
    runSelected: any;
  };
  private runSelectedData: any;
  private reportHistoryService = new ReportHistoryService();

  constructor(
    private $scope,
    private QueryModel,
    private $q,
    private ResultModel,
    private $timeout,
    private $window,
    private thoughtspotIntegrationService: ThoughtspotIntegrationService
  ) {
    this.state = {
      selectedTab: 'raw',
      runSelected: null,
    };

    $scope.$watch('ctrl.state.selectedTab', (tab) => {
      if (
        tab === 'data' &&
        this.state.runSelected &&
        this.state.runSelected.state === 'succeeded' &&
        !this.runSelectedData
      ) {
        this.selectedRunChanged();
      } else {
        this.runSelectedData = null;
      }
    });

    $scope.$watch('ctrl.state.runSelected', (run) => {
      if (run && this.state.selectedTab === 'data') {
        if (this.state.runSelected.state === 'succeeded') {
          this.selectedRunChanged();
        } else {
          this.runSelectedData = null;
        }
      }
    });
  }

  $onInit() {
    this.errorImage = errorGif;
    this.isAnalystStudio$ = this.thoughtspotIntegrationService.currOrgIntegrated$;

    const query = new this.QueryModel.model(this.query);
    query.queryRuns({ ...this.queryRunsParams }).then((queryRunsCollection) => {
      this.queryRunsCollection = queryRunsCollection;
      queryRunsCollection.watch(this.$scope, (queryRuns) => {
        this.includeExecutedBy(queryRuns)
          .then(this.reportHistoryService.fetchQueryRunResult)
          .then((queryRunsComplete) => {
            this.runPreview = queryRunsComplete.filter((qR) => !!qR);
            if (queryRunsComplete.length && !this.state.runSelected) {
              this.state.runSelected = queryRunsComplete[0];
            }
          });
      });
    });
  }

  includeExecutedBy(queryRuns) {
    return this.$q.all(
      _.chain(queryRuns)
        .sortBy('createdAt')
        .reverse()
        .map((queryRun) =>
          Object.assign(queryRun, {
            executedByUsername: queryRun.executedByUsername || queryRun._resource.embedded('executed_by').username,
          })
        )
        .value()
    );
  }

  refreshResultIfStale(result) {
    if (result.content) {
      return result;
    } else {
      return this.ResultModel.refresh(result);
    }
  }

  selectedRunChanged() {
    this.state.runSelected
      .result()
      .then(this.refreshResultIfStale.bind(this))
      .then((result) => {
        if (result.underBrowserSizeLimit()) {
          result.dataset().then((dataset) => {
            this.runSelectedData = dataset;
            this.$timeout(
              () => {
                $(this.$window).trigger('resize.grid');
              },
              0,
              false
            );
          });
        }
      })
      .catch((error) =>
        this.reportHistoryService.checkIfPurged(error, (purgedRun) => (this.runSelectedData = purgedRun))
      );
  }

  revert(queryRun) {
    this.onClose({ queryRun: queryRun });
  }

  selectRun(queryRun, $event) {
    this.state.runSelected = queryRun;
    $event.preventDefault();
    $event.stopPropagation();
  }

  showMore() {
    if (!this.queryRunsCollection) {
      return;
    }
    this.queryRunsCollection
      .next({ ...this.queryRunsParams })
      .then((queryRunsCollection) => {
        this.queryRunsCollection = queryRunsCollection;
        return queryRunsCollection.elements();
      })
      .then(this.includeExecutedBy.bind(this))
      .then(this.reportHistoryService.fetchQueryRunResult)
      .then((queryRuns) => {
        this.runPreview.concat(queryRuns);
      });
  }
}

const reportHistoryComponent: angular.IComponentOptions = {
  bindings: {
    onDismiss: '&',
    onClose: '&',
    query: '<',
    isDataset: '<',
  },
  template: `
<div class="history-overlay">
  <div class="history-header history-2022-toolbar">
    <button ng-click="ctrl.onDismiss()" class="history-close">
      <capra-icon icon="icon-close"></capra-icon>
    </button>
    <h3 class="history-2022-title">Query history</h3>
  </div>
  <div class="iframe-loading" ng-if="!ctrl.runPreview">
    <mode-loader></mode-loader>
  </div>
  <ul class="history-list" ng-if="ctrl.runPreview">
    <li ng-repeat="queryRun in ctrl.runPreview" ng-class="{ active: ctrl.state.runSelected === queryRun }">
      <a href="#" ng-click="ctrl.selectRun(queryRun, $event);">
        <capra-icon
          ng-if="queryRun.state === 'succeeded'"
          class="status-icon success"
          icon="icon-check"
        ></capra-icon>
        <capra-icon
          ng-if="queryRun.state === 'cancelled' || queryRun.state === 'failed'"
          ng-class="{ warning: queryRun.state === 'cancelled', error: queryRun.state === 'failed' }"
          class="status-icon"
          icon="icon-warning"
        ></capra-icon>
        <mode-time-ago class="run-time"
          [date]="queryRun.completed_at"
          format="capitalized"
        ></mode-time-ago>
        <span class="run-details" ng-if="queryRun.resultStats">{{ queryRun.resultStats.count }}</span>
        <span class="run-code">{{ queryRun.raw_source }}</span>
      </a>
    </li>
    <li class="show-more" ng-if="ctrl.queryRunsCollection && ctrl.queryRunsCollection.hasNext()">
      <capra-button
        ng-click="ctrl.showMore();"
        icon="icon-sort-descending-1"
        variant="secondary"
        color="neutral"
        size="small"
        text="Show more"
      >
      </capra-button>
    </li>
  </ul>
  <div class="history-preview" ng-if="ctrl.state.runSelected">
    <div class="history-2022-preview-header">
      <p class="history-2022-run-by">
        Run by <span>{{ ctrl.state.runSelected.executedByUsername }}</span>
        <span ng-if="ctrl.state.runSelected.state==='failed'"> and failed</span>
        <span ng-if="ctrl.state.runSelected.state==='cancelled'"> and cancelled</span>
        <span ng-if="ctrl.state.runSelected.resultStats"> and returned {{ ctrl.state.runSelected.resultStats.count }} rows</span>
      </p>
      <div class="history-2022-actions">
        <a
          ng-if="!ctrl.isDataset && !(ctrl.isAnalystStudio$ | async)"
          ng-href="{{ ctrl.state.runSelected.reportRunUrl }}"
          class="history-2022-ghost-button history-2022-action"
          target="_blank"
          rel="noopener">View Report using this run</a>
        <capra-button
          size="small"
          text="Insert SQL in editor"
          class="history-2022-action"
          (clicked)="ctrl.revert(ctrl.state.runSelected);"
        ></capra-button>
      </div>
    </div>
    <div class="history-preview-nav">
      <ul class="nav nav-tabs nav-tabs-bottom">
        <li ng-class="{active:ctrl.state.selectedTab === 'raw'}"><a href="#" ng-click="ctrl.state.selectedTab = 'raw'">Raw</a></li>
        <li ng-class="{active:ctrl.state.selectedTab === 'rendered'}"><a href="#" ng-click="ctrl.state.selectedTab = 'rendered'">Rendered</a></li>
        <li ng-class="{active:ctrl.state.selectedTab === 'data'}"><a href="#" ng-click="ctrl.state.selectedTab = 'data'">Data</a></li>
      </ul>
    </div>
    <div class="history-preview-content">
      <div ng-show="ctrl.state.selectedTab === 'raw'" mode-live-code lang="sql" line-numbers="true" code="ctrl.state.runSelected.raw_source"></div>
      <div ng-show="ctrl.state.selectedTab === 'rendered'" mode-live-code lang="sql" line-numbers="true" code="ctrl.state.runSelected.rendered_source"></div>
      <div class="history-data mode-grid-container" ng-class="{show:ctrl.state.selectedTab === 'data'}">
        <div class="editor-error" ng-if="ctrl.state.runSelected.state==='failed'">
          <img ng-src="{{ctrl.errorImage}}" class="error-gif">
          <h3>Looks like something went wrong with your query.</h3>
          <pre>{{ ctrl.state.runSelected.errorMessage }}</pre>
          <div class="error-lines" ng-bind-html="ctrl.state.runSelected.errorLineHtml"></div>
        </div>
        <dataset-grid dataset="ctrl.runSelectedData" ng-if="ctrl.state.runSelected.state==='succeeded'"></dataset-grid>
      </div>
    </div>
  </div>
</div>
`,
  controller: ReportHistoryController,
  controllerAs: 'ctrl',
};

angular.module('mode.components').component('reportHistory', reportHistoryComponent);
